import Vue from 'vue';
import VueRouter from 'vue-router';
import store from '@/store';
import AccountService from '@/services/AccountService';

async function requireAuth(to, from, next) {
	let isLoggedIn = AccountService.isUserSignedIn();

	// if not logged in, let's get the login form
	if (!isLoggedIn) {
		console.info('retreiving hosted login form');
		await AccountService.getSession();
	}

	// grab the tokens if either is missing
	if (isLoggedIn && (!store.getters.getAccessToken || !store.getters.getRefreshToken)) {
		await store.dispatch("getAccessToken");
		await store.dispatch("getRefreshToken");
	}

	// Get info about the user if we don't have it
	if (isLoggedIn && !store.getters.getLoggedInUserId) {
		await store.dispatch('getLoggedInUserInfo');
		await store.dispatch('getTenantList');
	}

	next(); // continue navigation
}

const routes = [
	{
		path: '/',
		redirect: 'clients', //temporary - will route to dashboard in the future
	},
	{
		path: '/dashboard',
		redirect: 'clients',
	},
	{
		path: '/clients/discard/review',
		name: 'Discard Review',
		component: () => import(/* webpackChunkName: "promoteReview" */ '../views/clients/discard/review'),
		beforeEnter: requireAuth
	},
	{
		path: '/clients/promote/review',
		name: 'Promote Review',
		component: () => import(/* webpackChunkName: "promoteReview" */ '../views/clients/promote/review'),
		beforeEnter: requireAuth
	},
	{
		path: '/clients/promote/summary',
		name: 'Promote Summary',
		component: () => import(/* webpackChunkName: "promoteSummary" */ '../views/clients/promote/summary'),
		beforeEnter: requireAuth
	},
	{
		path: '/clients/bulkUpdate',
		name: 'Bulk Update',
		component: () => import(/* webpackChunkName: "bulkUpdate" */ '../views/bulk/update'),
		beforeEnter: requireAuth
	},
	{
		path: '/clients/:type/summary',
		name: 'Bulk Update Summary',
		component: () => import(/* webpackChunkName: "bulkSummary" */ '../views/bulk/summary'),
		beforeEnter: requireAuth
	},
	{
		path: '/clients/bulkEdit/:tab?',
		name: 'Bulk Edit',
		component: () => import(/* webpackChunkName: "bulkEdit" */ '../views/bulk/edit'),
		beforeEnter: requireAuth
	},
	{
		path: '/clients/bulkDelete/:tab?',
		name: 'Bulk Delete',
		component: () => import(/* webpackChunkName: "bulkDelete" */ '../views/bulk/delete'),
		beforeEnter: requireAuth
	},
	{
		path: '/clients/templates/edit/:ruleId',
		name: 'Edit Template',
		component: () => import(/* webpackChunkName: "editTemplate" */ '../views/clients/templates/edit'),
		beforeEnter: requireAuth
	},
	{
		path: '/clients/templates/create',
		name: 'Create Template',
		component: () => import(/* webpackChunkName: "createTemplate" */ '../views/clients/templates/create'),
		beforeEnter: requireAuth
	},
	{
		path: '/clients/templates',
		name: 'Templates',
		component: () => import(/* webpackChunkName: "templates" */ '../views/clients/templates'),
		beforeEnter: async (to, from, next) => {
			await requireAuth(to, from, next)
			store.dispatch('listTemplates');
		}
	},
	{
		path: '/clients',
		name: 'Client Management',
		component: () => import(/* webpackChunkName: "clients" */ '../views/clients'),
		beforeEnter: async (to, from, next) => {
			await requireAuth(to, from, next)
			store.dispatch('list');
		}
	},
	{
		path: '/enableDebug',
		name: 'Debug',
		beforeEnter() {
			store.dispatch('enableDebugMode');
		}
	},
	{
		path: '/logout',
		name: 'Logout',
		redirect: '/login',
	},
	{
		path: '/login',
		name: 'Login',
		beforeEnter() {
			AccountService.getSession();
		}
	},
	{
		path: '/login/:quavoClient',
		name: 'Quavo Client Login',
		beforeEnter(to) {
			AccountService.getSession(to.params.quavoClient);
		}
	},
	{
		path: '/login/oauth2/code/cognito',
		beforeEnter(to) {
			//if we end up here but are already loggedin - just let the redirect handle it...
			if (!AccountService.isUserSignedIn()) {
				var currUrl = window.location.href;
				AccountService.parseCognitoResponse(currUrl);
				if (AccountService.isUserSignedIn()) {
					AccountService.getSession(to.params.quavoClient);
				}
			}
		}
	},
	{
		path: '*',
		name: 'NotFound',
		component: () => import(/* webpackChunkName: "NotFound" */ '../views/notfound.vue')
	}
];

Vue.use(VueRouter);

const router = new VueRouter({
	mode: 'history',
	base: process.env.BASE_URL,
	routes,
});


router.beforeEach((to, from, next) => {
	// store the intended location so we can put the user back where they meant to be
	let storageWindow = window.sessionStorage;
	let target = store.getters.getPostLoginUrl;

	/*
		If the to.path isn't cognito, then trust it.
		Otherwise, we'll check for a locally stored path.
		If neither works, we'll just trust whatever vue already has.
	*/
	if (!to.path.includes('/login')) {
		target = to.path;
	} else if (storageWindow.getItem('qvo.targetUrl')) {
		target = storageWindow.getItem('qvo.targetUrl');
	}

	// make extra sure we're not looping on the cognito screen
	if (target.includes('/login') || target.includes('/logout')) {
		target = "/";
	}

	// update the store & local storage to cover our bases
	store.dispatch('setPostLoginUrl', target);
	storageWindow.setItem('qvo.targetUrl', target);

	next();
});

export default router;